import React, { ReactNode } from 'react';

export interface MainLayoutProps {
  // Sidebar
  sidebar: ReactNode;

  // TopBar
  topBar: ReactNode;

  // Main content
  children: ReactNode;
}

/**
 * Shared main layout component
 * Provides consistent structure across qs2 and qs-status-frontend:
 * - Fixed sidebar (responsive)
 * - Sticky top bar
 * - Main content area with padding
 *
 * CRITICAL LAYOUT CONSTRAINTS:
 * This component MUST maintain a single-layer overflow structure to prevent
 * infinite resize loops in components that use ResizeObserver (e.g., Dashboard with ChartGrid).
 *
 * DO NOT add:
 * - overflow-auto to <main> element
 * - Nested wrapper divs with overflow-auto
 * - Multiple flex-1 layers
 *
 * WHY: Nested overflow-auto containers create a feedback loop:
 * 1. Charts render to fit container height
 * 2. Nested overflow containers see "extra space available"
 * 3. Containers expand to fill space
 * 4. ResizeObserver fires → triggers re-render
 * 5. Charts recalculate → Step 1 repeats infinitely
 *
 * The correct structure uses:
 * - flex-grow on <main> to fill available viewport height (constrained by html/body height: 100%)
 * - min-h-0 to allow flex children to shrink
 * - Children (Page component) handle their own overflow with overflow-auto
 * - This creates exactly ONE overflow layer in the hierarchy
 *
 * See: packages/shared-ui/docs/LAYOUT-OVERFLOW-ISSUE.md for full details
 */
export const MainLayout: React.FC<MainLayoutProps> = ({
  sidebar,
  topBar,
  children,
}) => {
  return (
    <>
      {/* Sidebar */}
      {sidebar}

      {/* CRITICAL: No overflow-auto here! Use flex-grow to fill viewport height.
          The viewport constraint (html/body height: 100%) provides the fixed boundary.
          Children (Page component) handle scrolling with their own overflow-auto.
          Padding is added here to provide spacing around content. */}
      <main className="flex-col flex-grow flex min-w-0 min-h-0 lg:pl-64">
        {/* Top bar */}
        {topBar}
        {/* Main content area - NO wrapper div, children render directly */}
        {children}
      </main>
    </>
  );
};
